// src/pages/landing-builder/index.jsx
import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../lib/authFetch';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useVisibilityRefresh } from '../../hooks/useVisibilityRefresh';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  Button, Input, Modal, ModalFooter, Badge,
  Card, CardHeader, CardTitle, CardContent,
} from '../../components/ui';
import { formatNumber } from '../../lib/utils';
import {
  ArrowLeft, Save, Eye, Layout, Smartphone, Monitor, Tablet,
  Plus, Settings, Layers, BarChart3, Sparkles, Target, Globe, Copy,
  Bell, MessageSquare, Users, ChevronLeft, ChevronRight, Zap, Menu, X,
} from 'lucide-react';
import toast from 'react-hot-toast';

import { SYSTEM_TEMPLATES, ColorPicker } from './shared';
import { SECTION_TYPES, SectionEditor, StandardPagePreview, ContainerEditor } from './StandardPageBuilder';
import { OptinFunnelPreview, OptinFunnelEditor } from './OptinFunnelBuilder';
import { QuizFunnelPreview, QuizFunnelEditor } from './QuizFunnelBuilder';

export default function LandingPageBuilder() {
  const { pageId, projectId: urlProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const projectId = urlProjectId || searchParams.get('projectId');
  const navigate = useNavigate();
  const { user } = useAuth();

  const isNew = !pageId || pageId === 'new';
  // Projects are gone and this builder now lives under /landing-pages/legacy,
  // so both of the old back-destinations resolve to the legacy list.
  const backUrl = '/landing-pages/legacy';

  const [loading, setLoading]         = useState(!isNew);
  const [saving, setSaving]           = useState(false);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [activeView, setActiveView]   = useState(() => searchParams.get('view') === 'analytics' && !isNew ? 'analytics' : 'editor');
  const [editingSection, setEditingSection] = useState(null);
  const [showTemplates, setShowTemplates]   = useState(false);
  const [showSettings, setShowSettings]     = useState(false);
  const [useCustomDomain, setUseCustomDomain] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [showPageFeatures, setShowPageFeatures] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [templates, setTemplates]       = useState([]);
  const [customDomains, setCustomDomains] = useState([]);

  // Preview tab for funnel builders (step1 | step2 | popup | page)
  const [previewTab, setPreviewTab] = useState('step1');

  // Collapsible section bar
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const [page, setPage] = useState({
    name: '', slug: '', custom_slug: '', status: 'draft', custom_domain_id: null,
    meta_title: '', meta_description: '', header_scripts: '', footer_scripts: '',
    page_config: {
      templateType: 'standard',
      theme: { primaryColor: '#10B981', backgroundColor: '#ffffff', textColor: '#1f2937', fontFamily: 'Inter, sans-serif' },
      sections: [],
      optin: {}, quiz: {},
      footer:       { enabled: false, customText: '', showPrivacyLink: false, showTermsLink: false, backgroundColor: '', textColor: '' },
      stickyHeader: { enabled: false, text: '', backgroundColor: '', textColor: '#ffffff', alignment: 'center', countdownEnabled: false, countdownEndDate: '', countdownExpiredText: '' },
      socialProof:  { enabled: false, text: 'people are viewing this page', minNumber: 10, maxNumber: 50, position: 'bottom-left', backgroundColor: '#1f2937', textColor: '#ffffff' },
      popup:        { enabled: false, trigger: 'timer', timerDelay: 5, title: '⏰ Wait!', message: "Don't miss out on this offer.", ctaText: 'Continue', ctaUrl: '', titleColor: '#dc2626', isTestCta: false },
    },
  });

  const config      = page.page_config || {};
  const templateType = config.templateType || 'standard';
  const theme        = config.theme || {};
  const sections     = config.sections || [];
  const footer       = config.footer       || {};
  const stickyHeader = config.stickyHeader || {};
  const socialProof  = config.socialProof  || {};
  const popup        = config.popup        || {};

  const updateTheme       = (k, v) => setPage(p => ({ ...p, page_config: { ...p.page_config, theme:        { ...p.page_config.theme,        [k]: v } } }));
  const updateFooter      = (k, v) => setPage(p => ({ ...p, page_config: { ...p.page_config, footer:       { ...p.page_config.footer,       [k]: v } } }));
  const updateStickyHeader= (k, v) => setPage(p => ({ ...p, page_config: { ...p.page_config, stickyHeader: { ...p.page_config.stickyHeader, [k]: v } } }));
  const updateSocialProof = (k, v) => setPage(p => ({ ...p, page_config: { ...p.page_config, socialProof:  { ...p.page_config.socialProof,  [k]: v } } }));
  const updatePopup       = (k, v) => setPage(p => ({ ...p, page_config: { ...p.page_config, popup:        { ...p.page_config.popup,        [k]: v } } }));

  // ── Load ─────────────────────────────────────────────────────────────────
  const loadPage = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await authFetch(`/api/ab?resource=pages&pageId=${pageId}`);
      const data = await res.json();
      if (data.success && data.page) {
        const pc = data.page.page_config || {};
        if (!pc.sections)     pc.sections = [];
        if (!pc.theme)        pc.theme    = { primaryColor: '#10B981', backgroundColor: '#ffffff', textColor: '#1f2937', fontFamily: 'Inter, sans-serif' };
        if (!pc.templateType) pc.templateType = 'standard';
        if (!pc.footer)       pc.footer       = { enabled: false, customText: '' };
        if (!pc.stickyHeader) pc.stickyHeader  = { enabled: false };
        if (!pc.socialProof)  pc.socialProof   = { enabled: false };
        if (!pc.popup)        pc.popup         = { enabled: false };
        if (!pc.optin)        pc.optin         = {};
        if (!pc.quiz)         pc.quiz          = {};
        setPage({ ...data.page, page_config: pc });
        setUseCustomDomain(!!data.page.custom_domain_id);
      } else { toast.error('Page not found'); navigate(backUrl); }
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [pageId, isNew, navigate, backUrl]);

  const loadCustomDomains = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase.from('custom_domains').select('id, domain, status').eq('user_id', user.id).eq('status', 'verified');
    setCustomDomains(data || []);
  }, [user?.id]);  // depend on user.id (primitive) not user object — prevents re-run on session refresh

  const loadTemplates = useCallback(async () => {
    if (!user?.id) return;
    const res = await authFetch(`/api/ab?resource=pages&userId=${user.id}&includeTemplates=true`);
    const data = await res.json();
    if (data.success) setTemplates(data.templates || []);
  }, [user?.id]);  // same — stable primitive dependency

  // loadPage runs ONLY on mount and when the page ID changes — never on user refresh
  useEffect(() => { loadPage(); }, [loadPage]);
  // loadCustomDomains and loadTemplates run independently — changes to user session
  // will NOT trigger loadPage and will NOT wipe unsaved edits
  useEffect(() => { loadCustomDomains(); loadTemplates(); }, [loadCustomDomains, loadTemplates]);

  // Re-fetch custom domains (and page) when returning to this tab
  // This fixes the bug where custom domains show empty after switching tabs
  useVisibilityRefresh(loadCustomDomains);

  // ── Save / Publish ────────────────────────────────────────────────────────
  const handleSave = async (publish = false) => {
    if (!page.name.trim()) { toast.error('Enter page name'); return; }
    setSaving(true);
    const tid = toast.loading(publish ? 'Publishing...' : 'Saving...');
    try {
      const res = await authFetch('/api/ab?resource=pages', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: isNew ? 'create' : 'update',
          userId: user.id, pageId: isNew ? undefined : pageId,
          projectId: isNew ? projectId : undefined,
          name: page.name, title: page.name,
          // slug is auto-generated server-side on create; never sent on update (it's immutable)
          custom_slug: page.custom_domain_id ? (page.custom_slug || null) : null,
          custom_domain_id: page.custom_domain_id, page_config: page.page_config,
          meta_title: page.meta_title, meta_description: page.meta_description,
          header_scripts: page.header_scripts, footer_scripts: page.footer_scripts,
          status: publish ? 'published' : page.status,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(publish ? 'Published!' : 'Saved!', { id: tid });
        if (isNew) navigate(`/landing-pages/legacy/${data.page.id}`, { replace: true });
        else setPage(p => ({ ...p, ...data.page, page_config: data.page.page_config || p.page_config }));
      } else toast.error(data.error || 'Failed', { id: tid });
    } catch { toast.error('Failed', { id: tid }); }
    finally { setSaving(false); }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Enter template name'); return; }
    const tid = toast.loading('Saving...');
    try {
      const res = await authFetch('/api/ab?resource=pages', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'save_template', userId: user.id, name: templateName, description: templateDesc, page_config: page.page_config }),
      });
      const data = await res.json();
      if (data.success) { toast.success('Saved!', { id: tid }); setShowSaveTemplate(false); setTemplateName(''); setTemplateDesc(''); loadTemplates(); }
      else toast.error(data.error, { id: tid });
    } catch { toast.error('Failed', { id: tid }); }
  };

  const applyTemplate = (t) => {
    const cfg = { ...(t.page_config || {}) };
    if (!cfg.sections)     cfg.sections = [];
    if (!cfg.theme)        cfg.theme    = { primaryColor: '#10B981', backgroundColor: '#ffffff', textColor: '#1f2937', fontFamily: 'Inter, sans-serif' };
    if (!cfg.templateType) cfg.templateType = templateType;
    if (!cfg.footer)       cfg.footer       = { enabled: false, customText: '' };
    if (!cfg.stickyHeader) cfg.stickyHeader  = { enabled: false };
    if (!cfg.socialProof)  cfg.socialProof   = { enabled: false };
    if (!cfg.popup)        cfg.popup         = { enabled: false };
    if (!cfg.optin)        cfg.optin         = {};
    if (!cfg.quiz)         cfg.quiz          = {};
    setPage(p => ({ ...p, page_config: cfg }));
    setShowTemplates(false);
    toast.success(`Applied "${t.name}"`);
  };

  // ── Sections ──────────────────────────────────────────────────────────────
  const SECTION_LIMIT = 20;
  const addSection = (type) => {
    const st = SECTION_TYPES[type]; if (!st) return;
    const currentCount = page.page_config.sections.length;
    if (currentCount >= SECTION_LIMIT) {
      toast.error(`Section limit reached (${SECTION_LIMIT} max). Delete a section to add more.`);
      return;
    }
    setPage(p => ({ ...p, page_config: { ...p.page_config, sections: [...p.page_config.sections, { id: `${type}-${Date.now()}`, type, config: { ...st.defaultConfig } }] } }));
  };
  const updateSection = (updated) => {
    setPage(p => ({ ...p, page_config: { ...p.page_config, sections: p.page_config.sections.map(s => s.id === updated.id ? updated : s) } }));
    if (editingSection?.id === updated.id) setEditingSection(updated);
  };

  const deleteSection = (id) => {
    setPage(p => ({ ...p, page_config: { ...p.page_config, sections: p.page_config.sections.filter(s => s.id !== id) } }));
    if (editingSection?.id === id) setEditingSection(null);
  };
  const moveSection = (from, to) => {
    const arr = [...sections]; arr.splice(to, 0, arr.splice(from, 1)[0]);
    setPage(p => ({ ...p, page_config: { ...p.page_config, sections: arr } }));
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );

  const filteredSystem = SYSTEM_TEMPLATES.filter(t => t.templateType === templateType);
  const filteredUser   = templates.filter(t => (t.page_config?.templateType || 'standard') === templateType);

  const defaultPageUrl = page.slug ? `https://pages.revhapi.com/${page.slug}` : null;
  const customDomainObj = customDomains.find(d => d.id === page.custom_domain_id);
  const customPageUrl = page.custom_domain_id && page.custom_slug
    ? `https://${customDomainObj?.domain}/${page.custom_slug}`
    : null;
  const pageUrl = customPageUrl || defaultPageUrl;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -m-6">

      {/* ── Top bar ── */}
      <div className="flex flex-col border-b border-border bg-card flex-shrink-0">
        {/* Row 1: back + name + badges */}
        <div className="flex items-center gap-2 px-3 py-2 min-w-0">
          <Link to={backUrl} className="p-1.5 hover:bg-muted rounded-lg flex-shrink-0"><ArrowLeft className="h-4 w-4" /></Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <input value={page.name} onChange={e => setPage(p => ({ ...p, name: e.target.value }))}
                className="text-sm font-semibold bg-transparent border-b border-transparent hover:border-border focus:border-primary focus:outline-none px-1 min-w-[80px] max-w-[160px] sm:max-w-none"
                placeholder="Page name" />
              {page.status === 'published' && <Badge className="bg-green-500 text-[10px] px-1.5 py-0.5">Published</Badge>}
              {templateType === 'optin' && <Badge className="bg-purple-500 text-[10px] px-1.5 py-0.5">2-Step</Badge>}
              {templateType === 'quiz'  && <Badge className="bg-orange-500 text-[10px] px-1.5 py-0.5">Quiz</Badge>}
            </div>
            {pageUrl && page.status === 'published' && (
              <div className="flex items-center gap-1 mt-0.5">
                <a href={pageUrl} target="_blank" rel="noopener noreferrer"
                  className="text-[10px] text-primary hover:underline flex items-center gap-1 truncate max-w-[200px]">
                  <Globe className="h-2.5 w-2.5 flex-shrink-0" />{pageUrl}
                </a>
                <button onClick={() => { navigator.clipboard.writeText(pageUrl); toast.success('Copied!'); }}
                  className="p-0.5 hover:bg-muted rounded flex-shrink-0">
                  <Copy className="h-2.5 w-2.5 text-muted-foreground" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: actions — scrollable on mobile */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 border-t border-border/50 overflow-x-auto">
          {!isNew && (
            <>
              <button onClick={() => setActiveView(v => v === 'analytics' ? 'editor' : 'analytics')}
                className={`p-1.5 rounded flex-shrink-0 ${activeView === 'analytics' ? 'bg-primary text-white' : 'hover:bg-muted'}`}>
                <BarChart3 className="h-4 w-4" />
              </button>
              <div className="flex border border-border rounded overflow-hidden flex-shrink-0">
                {[{ m: 'desktop', I: Monitor }, { m: 'tablet', I: Tablet }, { m: 'mobile', I: Smartphone }].map(({ m, I: Icon }) => (
                  <button key={m} onClick={() => setPreviewMode(m)}
                    className={`p-1.5 ${previewMode === m ? 'bg-primary text-white' : 'hover:bg-muted'}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
              {templateType === 'standard' && (
                <Button variant="outline" size="sm" onClick={() => setShowPageFeatures(true)} className="flex-shrink-0 text-xs h-7 px-2">
                  <Zap className="h-3 w-3 mr-1" />Features
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => { setUseCustomDomain(!!page.custom_domain_id); setShowSettings(true); }} className="flex-shrink-0 text-xs h-7 px-2">
                <Settings className="h-3 w-3 mr-1" />Settings
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate(true)} className="flex-shrink-0 text-xs h-7 px-2">
                <Layers className="h-3 w-3 mr-1" />Template
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving} className="flex-shrink-0 text-xs h-7 px-2">
            <Save className="h-3 w-3 mr-1" />Save
          </Button>
          <Button size="sm" onClick={() => handleSave(true)} disabled={saving} className="flex-shrink-0 text-xs h-7 px-2">
            <Eye className="h-3 w-3 mr-1" />Publish
          </Button>
        </div>
      </div>

      {/* ── Analytics ── */}
      {activeView === 'analytics' && !isNew && (
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" />Page Performance</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-3">
                  {[['Total Views', formatNumber(page.total_views || 0)], ['CTA Clicks', formatNumber(page.total_cta_clicks || 0)], ['Conversion Rate', (page.total_views || 0) > 0 ? (((page.total_cta_clicks || 0) / page.total_views) * 100).toFixed(1) + '%' : '0%']].map(([l, v]) => (
                    <div key={l} className="p-4 bg-muted rounded-lg text-center"><p className="text-sm text-muted-foreground">{l}</p><p className="text-3xl font-bold">{v}</p></div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* ── Editor ── */}
      {activeView === 'editor' && (
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">

          {/* ── Mobile: hamburger in the preview topbar area ── */}
          <div className="lg:hidden absolute top-0 left-0 z-30 p-1">
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="p-2 rounded-lg bg-card border border-border shadow-sm hover:bg-muted"
              title="Open sections panel"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          {/* ── Mobile overlay backdrop ── */}
          {mobileSidebarOpen && (
            <div
              className="lg:hidden fixed inset-0 bg-black/40 z-40"
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}

          {/* ── Left sidebar ── */}
          <div className={`
            border-r border-border bg-card flex-shrink-0 flex flex-col transition-all duration-200
            hidden lg:flex
            ${sidebarCollapsed ? 'w-10' : 'w-64'}
          `}>
            {/* Desktop collapse toggle */}
            <button
              onClick={() => setSidebarCollapsed(v => !v)}
              className="flex items-center justify-center p-2 border-b hover:bg-muted flex-shrink-0"
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>

            {!sidebarCollapsed && (
              <div className="flex-1 overflow-y-auto p-3 space-y-1">
                {/* Templates */}
                <button
                  onClick={() => setShowTemplates(true)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 mb-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 hover:from-primary/20 hover:to-primary/10 transition-all text-left group"
                >
                  <div className="p-1 bg-primary/15 rounded-md">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-primary">Templates</p>
                    <p className="text-[10px] text-muted-foreground leading-tight">Start from a preset</p>
                  </div>
                </button>

                {templateType === 'standard' && (
                  <>
                    <div className="flex items-center justify-between px-1 mb-1.5">
                      <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Add Sections</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        page.page_config.sections.length >= SECTION_LIMIT ? 'bg-red-500/15 text-red-500' : 'bg-muted text-muted-foreground'
                      }`}>
                        {page.page_config.sections.length}/{SECTION_LIMIT}
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {Object.entries(SECTION_TYPES).map(([type, info]) => (
                        <button key={type} onClick={() => addSection(type)}
                          disabled={page.page_config.sections.length >= SECTION_LIMIT}
                          className="w-full flex items-center gap-2.5 px-2.5 py-2 text-left rounded-lg hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed group">
                          <div className="w-6 h-6 flex items-center justify-center rounded-md bg-muted group-hover:bg-background flex-shrink-0 transition-colors">
                            <info.icon className="h-3.5 w-3.5 text-muted-foreground" />
                          </div>
                          <span className="text-xs font-medium text-foreground">{info.name}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}

                <div className="flex items-center px-1 mt-4 mb-1.5">
                  <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Theme</span>
                </div>
                {[{ k: 'primaryColor', l: 'Primary' }, { k: 'backgroundColor', l: 'Background' }, { k: 'textColor', l: 'Text' }].map(({ k, l }) => (
                  <div key={k} className="mb-2">
                    <label className="text-xs font-medium">{l}</label>
                    <div className="flex gap-2 mt-1">
                      <input type="color" value={theme[k] || '#000'} onChange={e => updateTheme(k, e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
                      <input value={theme[k] || ''} onChange={e => updateTheme(k, e.target.value)} className="flex-1 px-2 text-xs border border-border rounded bg-card text-foreground" />
                    </div>
                  </div>
                ))}
                <div className="mt-2">
                  <label className="text-xs font-medium">Font Family</label>
                  <select value={theme.fontFamily || 'Inter, sans-serif'} onChange={e => updateTheme('fontFamily', e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-border rounded text-xs bg-card text-foreground">
                    {['Inter, sans-serif', 'Georgia, serif', "'Times New Roman', serif", 'Arial, sans-serif', "'Segoe UI', sans-serif", 'Verdana, sans-serif', "'Courier New', monospace"].map(f => <option key={f} value={f}>{f.split(',')[0]}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* Collapsed: icon strip */}
            {sidebarCollapsed && templateType === 'standard' && (
              <div className="flex-1 overflow-y-auto py-2">
                {Object.entries(SECTION_TYPES).map(([type, info]) => (
                  <button key={type} onClick={() => addSection(type)} title={info.name}
                    disabled={page.page_config.sections.length >= SECTION_LIMIT}
                    className="w-full flex items-center justify-center p-2 hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed">
                    <info.icon className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* ── Mobile sidebar drawer ── */}
          <div className={`
            lg:hidden fixed inset-y-0 left-0 z-50 w-72 bg-card border-r border-border flex flex-col shadow-2xl
            transition-transform duration-300
            ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}>
            {/* Drawer header with close button well separated */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
              <p className="text-sm font-semibold">Page Sections</p>
              <button
                onClick={() => setMobileSidebarOpen(false)}
                className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Close panel"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {/* Templates */}
              <button
                onClick={() => { setShowTemplates(true); setMobileSidebarOpen(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 mb-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 hover:from-primary/20 hover:to-primary/10 transition-all text-left"
              >
                <div className="p-1 bg-primary/15 rounded-md">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-primary">Templates</p>
                  <p className="text-[10px] text-muted-foreground leading-tight">Start from a preset</p>
                </div>
              </button>

              {templateType === 'standard' && (
                <>
                  <div className="flex items-center justify-between px-1 mb-1.5">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Add Sections</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      page.page_config.sections.length >= SECTION_LIMIT ? 'bg-red-500/15 text-red-500' : 'bg-muted text-muted-foreground'
                    }`}>
                      {page.page_config.sections.length}/{SECTION_LIMIT}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {Object.entries(SECTION_TYPES).map(([type, info]) => (
                      <button key={type}
                        onClick={() => { addSection(type); setMobileSidebarOpen(false); }}
                        disabled={page.page_config.sections.length >= SECTION_LIMIT}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2.5 text-left rounded-lg hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                        <div className="w-7 h-7 flex items-center justify-center rounded-md bg-muted flex-shrink-0">
                          <info.icon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">{info.name}</p>
                          <p className="text-[10px] text-muted-foreground leading-tight">{info.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div className="flex items-center px-1 mt-4 mb-1.5">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Theme</span>
              </div>
              {[{ k: 'primaryColor', l: 'Primary' }, { k: 'backgroundColor', l: 'Background' }, { k: 'textColor', l: 'Text' }].map(({ k, l }) => (
                <div key={k} className="mb-2">
                  <label className="text-xs font-medium">{l}</label>
                  <div className="flex gap-2 mt-1">
                    <input type="color" value={theme[k] || '#000'} onChange={e => updateTheme(k, e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
                    <input value={theme[k] || ''} onChange={e => updateTheme(k, e.target.value)} className="flex-1 px-2 text-xs border border-border rounded bg-card text-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Preview area ── */}
          <div className="flex-1 min-h-0 lg:min-h-0 overflow-y-auto bg-muted p-4 pt-14 lg:pt-4 flex justify-center items-start relative">
            <div
              className={`bg-white dark:bg-white shadow-2xl transition-all duration-300 ${
                previewMode === 'desktop' ? 'w-full max-w-4xl'
                  : previewMode === 'tablet' ? 'w-full max-w-3xl lg:w-[768px]'
                  : 'w-full max-w-sm lg:w-[375px]'
              }`}
              style={{ backgroundColor: theme.backgroundColor, color: theme.textColor, fontFamily: theme.fontFamily }}
            >
              {templateType === 'standard' && (
                <StandardPagePreview
                  sections={sections} theme={theme} footer={footer}
                  stickyHeader={stickyHeader} socialProof={socialProof} popup={popup}
                  editingSection={editingSection} setEditingSection={setEditingSection}
                  onSectionMove={moveSection} onSectionDelete={deleteSection}
                  onSectionUpdate={updateSection}
                />
              )}
              {templateType === 'optin' && (
                <OptinFunnelPreview
                  config={config} theme={theme}
                  previewTab={previewTab}
                  onSetPreviewTab={setPreviewTab}
                />
              )}
              {templateType === 'quiz' && (
                <QuizFunnelPreview
                  config={config} theme={theme}
                  previewTab={previewTab}
                  onSetPreviewTab={setPreviewTab}
                />
              )}
            </div>
          </div>

          {/* ── Right sidebar ── */}
          {templateType === 'standard' && editingSection && (
            <div className="w-full lg:w-80 max-h-[50vh] lg:max-h-none border-t lg:border-t-0 lg:border-l border-border overflow-hidden bg-card flex-shrink-0">
              {editingSection.type === 'container' ? (
                <div className="h-full flex flex-col">
                  <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
                    <h3 className="font-semibold text-sm">Container</h3>
                    <button onClick={() => setEditingSection(null)} className="p-1 hover:bg-muted rounded">
                      <span className="h-4 w-4 text-muted-foreground">✕</span>
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <ContainerEditor
                      config={editingSection.config || {}}
                      onUpdate={cfg => updateSection({ ...editingSection, config: cfg })}
                    />
                  </div>
                </div>
              ) : (
                <SectionEditor section={editingSection} onUpdate={updateSection} onClose={() => setEditingSection(null)} />
              )}
            </div>
          )}
          {templateType === 'optin' && (
            <div className="w-full lg:w-80 max-h-[50vh] lg:max-h-none border-t lg:border-t-0 lg:border-l border-border overflow-hidden bg-card flex-shrink-0">
              <div className="p-4 border-b flex items-center gap-2"><Sparkles className="h-4 w-4 text-purple-500" /><h3 className="font-semibold text-sm">2-Step Funnel</h3></div>
              <div className="overflow-y-auto h-[calc(100%-52px)]"><OptinFunnelEditor config={config} onChange={cfg => setPage(p => ({ ...p, page_config: cfg }))} /></div>
            </div>
          )}
          {templateType === 'quiz' && (
            <div className="w-full lg:w-80 max-h-[50vh] lg:max-h-none border-t lg:border-t-0 lg:border-l border-border overflow-hidden bg-card flex-shrink-0">
              <div className="p-4 border-b flex items-center gap-2"><Target className="h-4 w-4 text-orange-500" /><h3 className="font-semibold text-sm">Quiz Funnel</h3></div>
              <div className="overflow-y-auto h-[calc(100%-52px)]"><QuizFunnelEditor config={config} onChange={cfg => setPage(p => ({ ...p, page_config: cfg }))} /></div>
            </div>
          )}
        </div>
      )}

      {/* ── PAGE FEATURES MODAL ── */}
      <Modal isOpen={showPageFeatures} onClose={() => setShowPageFeatures(false)} title="Page Features" size="xl">
        <div className="overflow-y-auto max-h-[70vh] space-y-8 pr-1">

          {/* Feature toggles row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'stickyHeader', label: 'Sticky Header', desc: 'Top bar with text & countdown', icon: Bell,         state: stickyHeader, update: updateStickyHeader },
              { key: 'footer',       label: 'Footer',        desc: 'Page footer with links',        icon: Layout,       state: footer,       update: updateFooter       },
              { key: 'popup',        label: 'Popup',         desc: 'Timed or exit-intent popup',    icon: MessageSquare,state: popup,        update: updatePopup        },
              { key: 'socialProof',  label: 'Social Proof',  desc: 'Live visitor counter badge',    icon: Users,        state: socialProof,  update: updateSocialProof  },
            ].map(({ label, desc, icon: Icon, state, update }) => (
              <div key={label} onClick={() => update('enabled', !state.enabled)}
                className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${state.enabled ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}>
                <div className="flex items-center justify-between mb-2">
                  <Icon className={`h-5 w-5 ${state.enabled ? 'text-primary' : 'text-muted-foreground'}`} />
                  <div className={`w-9 h-5 rounded-full transition-colors ${state.enabled ? 'bg-primary' : 'bg-muted'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white shadow mt-0.5 transition-transform ${state.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </div>
                <p className="font-semibold text-sm">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
              </div>
            ))}
          </div>

          {/* ── Sticky Header settings ── */}
          {stickyHeader.enabled && (
            <div className="border rounded-xl p-5 space-y-4">
              <h4 className="font-semibold flex items-center gap-2"><Bell className="h-4 w-4 text-primary" />Sticky Header</h4>
              <Input label="Announcement Text" value={stickyHeader.text || ''} onChange={e => updateStickyHeader('text', e.target.value)} placeholder="🔥 Limited time offer — ends tonight!" />
              <div className="grid grid-cols-2 gap-3">
                <ColorPicker label="Background" value={stickyHeader.backgroundColor} onChange={v => updateStickyHeader('backgroundColor', v)} placeholder="Primary color" />
                <ColorPicker label="Text Color" value={stickyHeader.textColor} onChange={v => updateStickyHeader('textColor', v)} placeholder="#ffffff" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Alignment</label>
                <div className="flex gap-2">{['left', 'center', 'right'].map(a => (
                  <button key={a} onClick={() => updateStickyHeader('alignment', a)}
                    className={`flex-1 py-2 border border-border rounded text-sm capitalize ${stickyHeader.alignment === a ? 'bg-primary text-white border-primary' : ''}`}>{a}</button>
                ))}</div>
              </div>
              {/* Countdown in sticky header */}
              <div className="border-t pt-4">
                <label className="flex items-center gap-2 mb-3 font-medium text-sm">
                  <input type="checkbox" checked={stickyHeader.countdownEnabled || false} onChange={e => updateStickyHeader('countdownEnabled', e.target.checked)} className="rounded" />
                  Add Countdown Timer to Header
                </label>
                {stickyHeader.countdownEnabled && (
                  <div className="space-y-3 pl-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Countdown End Date & Time</label>
                      <input type="datetime-local" value={stickyHeader.countdownEndDate || ''} onChange={e => updateStickyHeader('countdownEndDate', e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground" />
                    </div>
                    <Input label="Expired Text" value={stickyHeader.countdownExpiredText || ''} onChange={e => updateStickyHeader('countdownExpiredText', e.target.value)} placeholder="Offer ended" />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Footer settings ── */}
          {footer.enabled && (
            <div className="border rounded-xl p-5 space-y-4">
              <h4 className="font-semibold flex items-center gap-2"><Layout className="h-4 w-4 text-primary" />Footer</h4>
              <Input label="Footer Text" value={footer.customText || ''} onChange={e => updateFooter('customText', e.target.value)} placeholder="© 2024 Your Company" />
              <div className="grid grid-cols-2 gap-3">
                <ColorPicker label="Background" value={footer.backgroundColor} onChange={v => updateFooter('backgroundColor', v)} />
                <ColorPicker label="Text Color" value={footer.textColor} onChange={v => updateFooter('textColor', v)} />
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={footer.showPrivacyLink || false} onChange={e => updateFooter('showPrivacyLink', e.target.checked)} className="rounded" />Privacy Policy</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={footer.showTermsLink || false} onChange={e => updateFooter('showTermsLink', e.target.checked)} className="rounded" />Terms of Service</label>
              </div>
            </div>
          )}

          {/* ── Popup settings ── */}
          {popup.enabled && (
            <div className="border rounded-xl p-5 space-y-4">
              <h4 className="font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" />Popup</h4>
              <div>
                <label className="block text-sm font-medium mb-1">Trigger</label>
                <div className="flex gap-2">
                  {[{ v: 'timer', l: 'Timer (delay)' }, { v: 'exit', l: 'Exit Intent' }, { v: 'scroll', l: 'On Scroll' }].map(({ v, l }) => (
                    <button key={v} onClick={() => updatePopup('trigger', v)}
                      className={`flex-1 py-2 border rounded text-sm ${popup.trigger === v ? 'bg-primary text-white border-primary' : ''}`}>{l}</button>
                  ))}
                </div>
              </div>
              {popup.trigger === 'timer' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Show after (seconds): {popup.timerDelay || 5}s</label>
                  <input type="range" min="1" max="60" value={popup.timerDelay || 5} onChange={e => updatePopup('timerDelay', parseInt(e.target.value))} className="w-full" />
                </div>
              )}
              {popup.trigger === 'scroll' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Show after scroll (%): {popup.scrollPercent || 50}%</label>
                  <input type="range" min="10" max="90" value={popup.scrollPercent || 50} onChange={e => updatePopup('scrollPercent', parseInt(e.target.value))} className="w-full" />
                </div>
              )}
              <Input label="Popup Title" value={popup.title || ''} onChange={e => updatePopup('title', e.target.value)} placeholder="⏰ Wait!" />
              <ColorPicker label="Title Color" value={popup.titleColor} onChange={v => updatePopup('titleColor', v)} placeholder="#dc2626" />
              <div><label className="block text-sm font-medium mb-1">Message</label><textarea value={popup.message || ''} onChange={e => updatePopup('message', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg h-16 text-sm bg-card text-foreground" placeholder="Don't miss out..." /></div>
              <Input label="CTA Button Text" value={popup.ctaText || ''} onChange={e => updatePopup('ctaText', e.target.value)} placeholder="Continue" />
              <Input label="CTA URL" value={popup.ctaUrl || ''} onChange={e => updatePopup('ctaUrl', e.target.value)} placeholder="https://..." />
              <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-50 text-sm">
                <input type="checkbox" checked={popup.isTestCta || false} onChange={e => updatePopup('isTestCta', e.target.checked)} className="rounded" />
                <div><span className="font-medium">Enable for A/B Testing</span><p className="text-xs text-muted-foreground">Assign popup CTA to a test in Projects</p></div>
              </label>
            </div>
          )}

          {/* ── Social Proof settings ── */}
          {socialProof.enabled && (
            <div className="border rounded-xl p-5 space-y-4">
              <h4 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Social Proof</h4>
              <Input label="Message text" value={socialProof.text || ''} onChange={e => updateSocialProof('text', e.target.value)} placeholder="people are viewing this page" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Min number: {socialProof.minNumber || 10}</label>
                  <input type="range" min="1" max="100" value={socialProof.minNumber || 10} onChange={e => updateSocialProof('minNumber', parseInt(e.target.value))} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max number: {socialProof.maxNumber || 50}</label>
                  <input type="range" min="1" max="500" value={socialProof.maxNumber || 50} onChange={e => updateSocialProof('maxNumber', parseInt(e.target.value))} className="w-full" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Position</label>
                <div className="flex gap-2">
                  {[{ v: 'bottom-left', l: 'Bottom Left' }, { v: 'bottom-right', l: 'Bottom Right' }].map(({ v, l }) => (
                    <button key={v} onClick={() => updateSocialProof('position', v)}
                      className={`flex-1 py-2 border rounded text-sm ${socialProof.position === v ? 'bg-primary text-white border-primary' : ''}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <ColorPicker label="Background" value={socialProof.backgroundColor} onChange={v => updateSocialProof('backgroundColor', v)} placeholder="#1f2937" />
                <ColorPicker label="Text Color" value={socialProof.textColor} onChange={v => updateSocialProof('textColor', v)} placeholder="#ffffff" />
              </div>
            </div>
          )}

          {/* ── Optin Funnel: Urgency Popup settings ── */}
          {templateType === 'optin' && (() => {
            const optin = config.optin || {};
            const updateOptin = (k, v) => setPage(p => ({ ...p, page_config: { ...p.page_config, optin: { ...p.page_config.optin, [k]: v } } }));
            return (
              <>
                {/* Toggle card */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { key: 'popupEnabled',       label: 'Urgency Popup',   desc: 'Timed popup on step 2',         icon: MessageSquare, val: optin.popupEnabled       || false },
                    { key: 'socialProofEnabled', label: 'Social Proof',    desc: 'Live signup notifications',     icon: Users,         val: optin.socialProofEnabled || false },
                  ].map(({ key, label, desc, icon: Icon, val }) => (
                    <div key={key} onClick={() => updateOptin(key, !val)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${val ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <Icon className={`h-5 w-5 ${val ? 'text-primary' : 'text-muted-foreground'}`} />
                        <div className={`w-9 h-5 rounded-full transition-colors ${val ? 'bg-primary' : 'bg-muted'}`}>
                          <div className={`w-4 h-4 rounded-full bg-white shadow mt-0.5 transition-transform ${val ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </div>
                      </div>
                      <p className="font-semibold text-sm">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                  ))}
                </div>

                {/* Popup settings */}
                {optin.popupEnabled && (
                  <div className="border rounded-xl p-5 space-y-4">
                    <h4 className="font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" />Urgency Popup</h4>
                    <p className="text-xs text-muted-foreground">Shown after visitor spends time on Step 2. Preview it in the Popup tab.</p>
                    <Input label="Popup Title" value={optin.popupTitle || ''} onChange={e => updateOptin('popupTitle', e.target.value)} placeholder="⏰ Hurry up, time will soon run out!" />
                    <div><label className="block text-sm font-medium mb-1">Popup Message</label>
                      <textarea value={optin.popupMessage || ''} onChange={e => updateOptin('popupMessage', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg h-16 text-sm bg-card text-foreground" placeholder="Accept now to continue your application." /></div>
                    <Input label="CTA Text" value={optin.popupCtaText || ''} onChange={e => updateOptin('popupCtaText', e.target.value)} placeholder="👉 Accept Now" />
                    <Input label="Show after (seconds on Step 2)" type="number" value={optin.popupDelay || 8} onChange={e => updateOptin('popupDelay', parseInt(e.target.value) || 8)} />
                    <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-50 text-sm">
                      <input type="checkbox" checked={optin.popupIsTestCta || false} onChange={e => updateOptin('popupIsTestCta', e.target.checked)} className="rounded" />
                      <div><span className="font-medium">Enable for A/B Testing</span><p className="text-xs text-muted-foreground">Assign popup CTA to a test</p></div>
                    </label>
                    <p className="text-xs text-muted-foreground bg-muted/40 p-2 rounded">💡 Popup uses the same destination URL as the Final CTA</p>
                  </div>
                )}

                {/* Social Proof settings */}
                {optin.socialProofEnabled && (
                  <div className="border rounded-xl p-5 space-y-4">
                    <h4 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Social Proof Notifications</h4>
                    <Input label="Message" value={optin.socialProofMessage || ''} onChange={e => updateOptin('socialProofMessage', e.target.value)} placeholder="just signed up" />
                    <div><label className="block text-sm font-medium mb-1">Names (one per line)</label>
                      <textarea value={(optin.socialProofNames || []).join('\n')} onChange={e => updateOptin('socialProofNames', e.target.value.split('\n').filter(n => n.trim()))} className="w-full px-3 py-2 border border-border rounded-lg h-20 text-sm bg-card text-foreground" placeholder="Chinedu\nAmina\nBlessing" /></div>
                    <div><label className="block text-sm font-medium mb-1">Locations (one per line)</label>
                      <textarea value={(optin.socialProofLocations || []).join('\n')} onChange={e => updateOptin('socialProofLocations', e.target.value.split('\n').filter(l => l.trim()))} className="w-full px-3 py-2 border border-border rounded-lg h-20 text-sm bg-card text-foreground" placeholder="Lagos\nAbuja\nPort Harcourt" /></div>
                    <Input label="Show every (seconds)" type="number" value={optin.socialProofInterval || 10} onChange={e => updateOptin('socialProofInterval', parseInt(e.target.value) || 10)} />
                  </div>
                )}
              </>
            );
          })()}

          {/* ── Quiz Funnel: Popup + Social Proof settings ── */}
          {templateType === 'quiz' && (() => {
            const quiz = config.quiz || {};
            const updateQuiz = (k, v) => setPage(p => ({ ...p, page_config: { ...p.page_config, quiz: { ...p.page_config.quiz, [k]: v } } }));
            return (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { key: 'popupEnabled',       label: 'Urgency Popup', desc: 'Popup after quiz completion', icon: MessageSquare, val: quiz.popupEnabled       || false },
                    { key: 'socialProofEnabled', label: 'Social Proof',  desc: 'Live completion badges',     icon: Users,         val: quiz.socialProofEnabled || false },
                  ].map(({ key, label, desc, icon: Icon, val }) => (
                    <div key={key} onClick={() => updateQuiz(key, !val)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${val ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <Icon className={`h-5 w-5 ${val ? 'text-primary' : 'text-muted-foreground'}`} />
                        <div className={`w-9 h-5 rounded-full transition-colors ${val ? 'bg-primary' : 'bg-muted'}`}>
                          <div className={`w-4 h-4 rounded-full bg-white shadow mt-0.5 transition-transform ${val ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </div>
                      </div>
                      <p className="font-semibold text-sm">{label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                    </div>
                  ))}
                </div>

                {quiz.popupEnabled && (
                  <div className="border rounded-xl p-5 space-y-4">
                    <h4 className="font-semibold flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" />Urgency Popup</h4>
                    <Input label="Popup Title" value={quiz.popupTitle || ''} onChange={e => updateQuiz('popupTitle', e.target.value)} placeholder="⏰ Wait, don't leave!" />
                    <div><label className="block text-sm font-medium mb-1">Popup Message</label>
                      <textarea value={quiz.popupMessage || ''} onChange={e => updateQuiz('popupMessage', e.target.value)} className="w-full px-3 py-2 border border-border rounded-lg h-16 text-sm bg-card text-foreground" placeholder="You're almost done..." /></div>
                    <Input label="CTA Text" value={quiz.popupCtaText || ''} onChange={e => updateQuiz('popupCtaText', e.target.value)} placeholder="Continue" />
                    <Input label="Show after (seconds)" type="number" value={quiz.popupDelay || 8} onChange={e => updateQuiz('popupDelay', parseInt(e.target.value) || 8)} />
                    <label className="flex items-center gap-2 p-3 border rounded-lg bg-purple-50 text-sm">
                      <input type="checkbox" checked={quiz.popupIsTestCta || false} onChange={e => updateQuiz('popupIsTestCta', e.target.checked)} className="rounded" />
                      <div><span className="font-medium">Enable for A/B Testing</span></div>
                    </label>
                  </div>
                )}

                {quiz.socialProofEnabled && (
                  <div className="border rounded-xl p-5 space-y-4">
                    <h4 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Social Proof</h4>
                    <Input label="Message" value={quiz.socialProofMessage || ''} onChange={e => updateQuiz('socialProofMessage', e.target.value)} placeholder="just completed this quiz" />
                    <div><label className="block text-sm font-medium mb-1">Names (one per line)</label>
                      <textarea value={(quiz.socialProofNames || []).join('\n')} onChange={e => updateQuiz('socialProofNames', e.target.value.split('\n').filter(n => n.trim()))} className="w-full px-3 py-2 border border-border rounded-lg h-20 text-sm bg-card text-foreground" placeholder="Emeka\nFatima\nChidi" /></div>
                    <div><label className="block text-sm font-medium mb-1">Locations (one per line)</label>
                      <textarea value={(quiz.socialProofLocations || []).join('\n')} onChange={e => updateQuiz('socialProofLocations', e.target.value.split('\n').filter(l => l.trim()))} className="w-full px-3 py-2 border border-border rounded-lg h-20 text-sm bg-card text-foreground" placeholder="Lagos\nAbuja\nKano" /></div>
                    <Input label="Show every (seconds)" type="number" value={quiz.socialProofInterval || 10} onChange={e => updateQuiz('socialProofInterval', parseInt(e.target.value) || 10)} />
                  </div>
                )}
              </>
            );
          })()}

        </div>
        <ModalFooter><Button onClick={() => setShowPageFeatures(false)}>Done</Button></ModalFooter>
      </Modal>

      {/* ── Templates modal ── */}
      <Modal isOpen={showTemplates} onClose={() => setShowTemplates(false)} title="Choose Template" size="xl">
        <div className="mb-3 flex items-center gap-2 p-3 bg-muted rounded-lg text-sm">
          {templateType === 'standard' && <><Layout className="h-4 w-4 text-blue-500" /><span className="font-medium">Standard Page templates</span></>}
          {templateType === 'optin'    && <><Sparkles className="h-4 w-4 text-purple-500" /><span className="font-medium">2-Step Funnel templates</span></>}
          {templateType === 'quiz'     && <><Target className="h-4 w-4 text-orange-500" /><span className="font-medium">Quiz Funnel templates</span></>}
          <span className="ml-auto text-xs text-muted-foreground">Other types hidden</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[50vh] overflow-y-auto">
          <div onClick={() => { setPage(p => ({ ...p, page_config: { ...p.page_config, sections: [] } })); setShowTemplates(false); }}
            className="border-2 border-dashed rounded-lg p-8 cursor-pointer hover:border-primary text-center">
            <Plus className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
            <p className="font-medium">Blank</p><p className="text-sm text-muted-foreground">Start from scratch</p>
          </div>
          {filteredSystem.map(t => (
            <div key={t.id} onClick={() => applyTemplate(t)} className="border rounded-lg overflow-hidden cursor-pointer hover:border-primary hover:shadow-md transition-all">
              <div className="aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"><Layout className="h-8 w-8 text-muted-foreground opacity-40" /></div>
              <div className="p-3"><h4 className="font-medium truncate text-sm">{t.name}</h4><p className="text-xs text-muted-foreground truncate">{t.description}</p></div>
            </div>
          ))}
          {filteredUser.map(t => (
            <div key={t.id} onClick={() => applyTemplate(t)} className="border rounded-lg overflow-hidden cursor-pointer hover:border-primary hover:shadow-md transition-all">
              <div className="aspect-video bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center"><Layers className="h-8 w-8 text-purple-300" /></div>
              <div className="p-3">
                <div className="flex items-center gap-2"><h4 className="font-medium truncate flex-1 text-sm">{t.name}</h4><Badge variant="secondary" className="text-xs">Saved</Badge></div>
                <p className="text-xs text-muted-foreground truncate">{t.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* ── Save template modal ── */}
      <Modal isOpen={showSaveTemplate} onClose={() => setShowSaveTemplate(false)} title="Save as Template">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">Saved under <strong>{templateType === 'standard' ? 'Standard Page' : templateType === 'optin' ? '2-Step Funnel' : 'Quiz Funnel'}</strong> templates.</p>
          <Input label="Template Name" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="My Template" />
          <div><label className="block text-sm font-medium mb-1">Description</label><textarea value={templateDesc} onChange={e => setTemplateDesc(e.target.value)} className="w-full px-3 py-2 border rounded-lg h-20 text-sm" /></div>
        </div>
        <ModalFooter><Button variant="outline" onClick={() => setShowSaveTemplate(false)}>Cancel</Button><Button onClick={handleSaveTemplate}>Save</Button></ModalFooter>
      </Modal>

      {/* ── Page settings modal ── */}
      <Modal isOpen={showSettings} onClose={() => setShowSettings(false)} title="Page Settings">
        <div className="space-y-4 overflow-y-auto max-h-[65vh] pr-1">
          {/* Default domain — always shown, always read-only */}
          <div>
            <label className="block text-sm font-medium mb-1">Default URL</label>
            <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-muted/40 text-sm font-mono text-muted-foreground">
              <span className="text-muted-foreground/60 flex-shrink-0">pages.revhapi.com/</span>
              <span className="truncate flex-1 text-foreground">{page.slug || '—'}</span>
              <span className="text-xs bg-muted px-1.5 py-0.5 rounded flex-shrink-0">Fixed</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Auto-generated and permanent. Cannot be changed.</p>
          </div>

          {/* Custom domain — opt-in via checkbox */}
          <div className="border border-border rounded-lg p-3 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={useCustomDomain}
                onChange={e => {
                  const checked = e.target.checked;
                  setUseCustomDomain(checked);
                  if (!checked) {
                    // Unchecking — clear domain from page state
                    setPage(p => ({ ...p, custom_domain_id: null, custom_slug: '' }));
                  } else if (customDomains.length > 0) {
                    // Checking with domains available — pre-select first domain
                    setPage(p => ({ ...p, custom_domain_id: customDomains[0].id, custom_slug: p.custom_slug || '' }));
                  }
                  // If checking with no domains: checkbox stays checked, shows the error message below
                }}
                className="rounded"
              />
              <span className="text-sm font-medium">Also publish on a custom domain</span>
            </label>

            {useCustomDomain && (
              <div className="space-y-3 pt-1">
                {customDomains.length === 0 ? (
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                    <span className="flex-shrink-0 mt-0.5">⚠️</span>
                    <span>No verified custom domains found. Add and verify one in <strong>Settings → Custom Domains</strong>, then come back here.</span>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Domain</label>
                      <select
                        value={page.custom_domain_id || ''}
                        onChange={e => setPage(p => ({ ...p, custom_domain_id: e.target.value || null, custom_slug: '' }))}
                        className="w-full px-3 py-2 border border-border rounded-lg text-sm bg-card text-foreground"
                      >
                        <option value="">— Select a domain —</option>
                        {customDomains.map(d => <option key={d.id} value={d.id}>{d.domain}</option>)}
                      </select>
                    </div>
                    {page.custom_domain_id && (
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">
                          Slug on this domain <span className="text-muted-foreground font-normal">(optional)</span>
                        </label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {customDomainObj?.domain}/
                          </span>
                          <input
                            type="text"
                            value={page.custom_slug || ''}
                            onChange={e => setPage(p => ({ ...p, custom_slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                            placeholder="your-page"
                            className="flex-1 px-2 py-1.5 border border-border rounded-lg text-sm bg-card text-foreground font-mono"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">Leave empty to serve on the root of your domain.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <Input label="Meta Title" value={page.meta_title || ''} onChange={e => setPage(p => ({ ...p, meta_title: e.target.value }))} />
          <div><label className="block text-sm font-medium mb-1">Meta Description</label><textarea value={page.meta_description || ''} onChange={e => setPage(p => ({ ...p, meta_description: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg h-20 text-sm bg-card text-foreground" /></div>
          <div><label className="block text-sm font-medium mb-1">Header Scripts</label><textarea value={page.header_scripts || ''} onChange={e => setPage(p => ({ ...p, header_scripts: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg h-20 font-mono text-xs bg-card text-foreground" placeholder="<script>...</script>" /></div>
          <div><label className="block text-sm font-medium mb-1">Footer Scripts</label><textarea value={page.footer_scripts || ''} onChange={e => setPage(p => ({ ...p, footer_scripts: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg h-20 font-mono text-xs bg-card text-foreground" /></div>
        </div>
        <ModalFooter><Button onClick={() => setShowSettings(false)}>Done</Button></ModalFooter>
      </Modal>
    </div>
  );
}
